import { useState } from 'react';
import { Eye, EyeOff, Loader2, Lock, ChevronDown } from 'lucide-react';
import { api, clearSession, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

// Mirror the backend policy exactly (server.py change_password):
// >= 15 chars, <= 72 UTF-8 bytes, new != current. The password value is never
// persisted to localStorage and never logged.
const MIN_LEN = 10;
const MAX_BYTES = 72;
const byteLen = (s) => new TextEncoder().encode(s).length;

function PwInput({ testid, label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="as-label">{label}</label>
      <div className="mt-2 flex items-center rounded-xl border border-white/[0.06] bg-black/30 transition-colors focus-within:border-white/20">
        <input
          data-testid={testid}
          className="h-14 flex-1 bg-transparent px-4 text-[15px] text-white placeholder:text-asgym-muted outline-none"
          type={show ? 'text' : 'password'}
          placeholder="••••••••"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={() => setShow((s) => !s)}
          className="px-4 text-asgym-sub"
        >
          {show ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePasswordCard({ defaultOpen = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(defaultOpen);
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!cur || !nw || !cf) return setError(t('pw.errFillAll'));
    if (nw.length < MIN_LEN) return setError(t('pw.errMinLen'));
    if (byteLen(nw) > MAX_BYTES) return setError(t('pw.errMaxLen'));
    if (nw === cur) return setError(t('pw.errSame'));
    if (nw !== cf) return setError(t('pw.errMismatch'));
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: nw });
      // The old JWT is now invalid (server bumped token_version). Drop the local
      // session and return to login; never keep any password value around.
      setCur(''); setNw(''); setCf('');
      clearSession();
      sessionStorage.setItem('pw_changed', '1');
      window.location.href = '/';
    } catch (err) {
      setError(errMsg(err, t('pw.errGeneric')));
      setLoading(false);
    }
  };

  return (
    <div className="as-card px-4 py-4" data-testid="change-password-card">
      <button
        type="button"
        data-testid="change-password-toggle"
        onClick={() => { setOpen((o) => !o); setError(''); }}
        className="flex w-full items-center justify-between"
      >
        <span className="flex items-center gap-2.5 text-sm font-extrabold tracking-wide text-white">
          <Lock size={18} className="text-asgym-accent" /> {t('pw.title')}
        </span>
        <ChevronDown size={18} className={`text-asgym-sub transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="change-password-form">
          <PwInput testid="pw-current" label={t('pw.current')} value={cur} onChange={setCur} />
          <PwInput testid="pw-new" label={t('pw.new')} value={nw} onChange={setNw} />
          <PwInput testid="pw-confirm" label={t('pw.confirm')} value={cf} onChange={setCf} />
          <p className="text-[12px] text-asgym-muted">{t('pw.hint')}</p>
          {error && (
            <p data-testid="pw-error" className="text-[13px] font-semibold text-asgym-error">{error}</p>
          )}
          <button type="submit" data-testid="pw-submit" disabled={loading} className="as-btn-primary w-full">
            {loading ? <Loader2 className="animate-spin" size={20} /> : t('pw.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
